import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OwnerBusinessesPage from "../OwnerBusinessesPage";
import { db as defaultDb, auth } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { isIosCapacitor } from "../utils/nativeHttp";
import fsClient from "../data/firestoreClient";
import { listOwnedBusinesses } from "../services/userBusinesses";
import {
  callLoopOwnerBusinessAdmin,
  getLoopEffectiveAuthIdentity,
  isLoopPlatformOwnerIdentity,
} from "../utils/platformOwner";

export default function OwnerPage({ user: userProp, db = defaultDb }) {
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadBusinesses = async () => {
      setLoading(true);
      setError(null);
      setBusinesses([]);

      try {
        const identity = getLoopEffectiveAuthIdentity(auth, userProp);
        const uid = identity?.uid || null;
        const email = identity?.email || "";
        const isPlatformOwner = isLoopPlatformOwnerIdentity(identity);

        if (!uid && !email) {
          if (mounted) {
            setBusinesses([]);
            setLoading(false);
            setError("Inicia sesión para ver tus empresas.");
          }
          return;
        }

        // The platform owner manages every company. Use the verified Admin SDK
        // reader first so iOS/manualAuth and Firestore rule drift cannot hide rows.
        if (isPlatformOwner) {
          try {
            const result = await callLoopOwnerBusinessAdmin({
              db,
              auth,
              user: userProp,
              action: "listBusinesses",
              limit: 2000,
            });
            const allBusinesses = Array.isArray(result?.businesses) ? result.businesses : [];
            if (mounted) {
              setBusinesses(allBusinesses);
              setLoading(false);
              if (!allBusinesses.length) setError("No encontré empresas en la plataforma.");
            }
            return;
          } catch (serverError) {
            console.warn("[OwnerPage] platform owner server list failed; trying client reader", serverError);
          }
        }

        // Web/Android path for a normal business owner.
        if (!isIosCapacitor()) {
          const col = collection(db, "businesses");
          const resultMap = new Map();

          if (isPlatformOwner) {
            const allSnap = await getDocs(col);
            allSnap.forEach((d) => resultMap.set(d.id, { id: d.id, ...(d.data() || {}) }));
          } else {
            const owned = await listOwnedBusinesses(db, { uid, email });
            owned.forEach(row => resultMap.set(row.id, row));
          }

          const results = Array.from(resultMap.values());
          if (mounted) {
            setBusinesses(results);
            setLoading(false);
            if (results.length === 0) setError("No encontré empresas para tu cuenta.");
          }
          return;
        }

        // iOS path for a normal business owner.
        let results = [];

        if (isPlatformOwner) {
          results = await fsClient.listCollectionByPath("businesses", { limit: 2000 }, { db });
        } else {
          results = await listOwnedBusinesses(db, { uid, email });
        }

        if (mounted) {
          setBusinesses(Array.isArray(results) ? results : []);
          setLoading(false);
          if (!results || results.length === 0) setError("No encontré empresas para tu cuenta.");
        }
      } catch (err) {
        console.error("Error loading owner businesses", err);
        if (mounted) {
          setBusinesses([]);
          setError(String(err && err.message ? err.message : err));
          setLoading(false);
        }
      }
    };

    loadBusinesses();
    return () => {
      mounted = false;
    };
  }, [userProp, db]);

  const handleViewCalendar = (biz) => {
    navigate(`/admin-calendar/${biz.id}`);
  };

  const handleGoToBooking = (biz) => {
    navigate("/booking", { state: { businessId: biz.id, businessName: biz.name } });
  };

  const handleEditBusiness = (biz) => {
    navigate(`/business/${biz.id}/edit`);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <OwnerBusinessesPage
        businesses={businesses}
        loading={loading}
        onGoBack={() => navigate(-1)}
        onViewCalendar={handleViewCalendar}
        onGoToBooking={handleGoToBooking}
        onEditBusiness={handleEditBusiness}
      />
      {error && <div style={{ color: "#b91c1c", marginTop: 12 }}>{error}</div>}
    </div>
  );
}
